// Text wrapping utilities for canvas rendering

interface TextLine {
  text: string
  width: number
}

// Wrap text to fit within a maximum width
export const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  fontSize: number = 16
): TextLine[] => {
  // Set font for measuring (bold for accurate measurement)
  ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`

  // Split text by spaces to get words
  const words = text.split(' ')
  const lines: TextLine[] = []
  let currentLine = ''

  for (let i = 0; i < words.length; i++) {
    const word = words[i]
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const metrics = ctx.measureText(testLine)

    if (metrics.width > maxWidth && currentLine) {
      // Current line is too long, save it and start new line
      lines.push({
        text: currentLine,
        width: ctx.measureText(currentLine).width
      })
      currentLine = word
    } else {
      // Add word to current line
      currentLine = testLine
    }
  }

  // Add remaining text
  if (currentLine) {
    lines.push({
      text: currentLine,
      width: ctx.measureText(currentLine).width
    })
  }

  return lines
}

// Calculate the best text layout for a rectangular area
export const calculateTextLayout = (
  ctx: CanvasRenderingContext2D,
  text: string,
  width: number,
  height: number,
  fontSize: number = 16,
  padding: number = 10
): {
  lines: TextLine[]
  fontSize: number
  totalHeight: number
} => {
  const maxWidth = width - (padding * 2)
  const maxHeight = height - (padding * 2)

  let adjustedFontSize = fontSize
  let lines: TextLine[] = []
  let totalHeight = 0
  const lineHeight = 1.2 // Line height multiplier

  // Try to fit text, reducing font size if needed
  while (adjustedFontSize >= 10) { // Minimum font size of 10
    lines = wrapText(ctx, text, maxWidth, adjustedFontSize)
    totalHeight = lines.length * adjustedFontSize * lineHeight

    // Check if text fits
    if (totalHeight <= maxHeight) {
      break
    }

    // Reduce font size and try again
    adjustedFontSize -= 2
  }

  return {
    lines,
    fontSize: adjustedFontSize,
    totalHeight
  }
}

// Draw wrapped text centered in a rectangle and return bottom position
export const drawWrappedText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  width: number,
  height: number,
  fontSize: number = 16,
  color: string = '#333',
  hideLabel: boolean = false
): number => {
  if (hideLabel || !text) return y + height / 2

  const padding = 10
  const layout = calculateTextLayout(ctx, text, width, height, fontSize, padding)

  // Set text properties
  ctx.fillStyle = color
  ctx.font = `bold ${layout.fontSize}px system-ui, -apple-system, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  // Calculate starting Y position to center text vertically
  const lineHeight = layout.fontSize * 1.2
  const startY = y + (height - layout.totalHeight) / 2 + lineHeight / 2

  // Draw each line with white outline
  layout.lines.forEach((line, index) => {
    const lineY = startY + (index * lineHeight)

    // Draw white outline first
    ctx.strokeStyle = 'white'
    ctx.lineWidth = 4
    ctx.lineJoin = 'round'
    ctx.strokeText(line.text, x + width / 2, lineY)

    // Then draw the fill text on top
    ctx.fillText(line.text, x + width / 2, lineY)
  })

  // Return the bottom Y position of the text
  return startY + (layout.lines.length - 1) * lineHeight + lineHeight / 2
}

// Helper to measure text dimensions
export const measureTextDimensions = (
  ctx: CanvasRenderingContext2D,
  text: string,
  fontSize: number = 16
): { width: number; height: number } => {
  ctx.font = `${fontSize}px system-ui, -apple-system, sans-serif`
  const metrics = ctx.measureText(text)

  return {
    width: metrics.width,
    height: fontSize * 1.2 // Approximate height with line height
  }
}